"use client"

import { useState, useEffect } from "react"
import { createPortal } from "react-dom"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Box,
  Typography,
  IconButton,
  TextField,
  Popover,
  LinearProgress,
  useTheme,
  Paper,
} from "@mui/material"
import { Anchor as AnchorIcon, Close as CloseIcon, Palette as PaletteIcon, CheckCircle } from "@mui/icons-material"
import { devicesActions, geofencesActions } from '../../store';

const SuccessToast = ({ open, message, onClose }) => {
  const theme = useTheme()
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    if (!open) return setProgress(0)

    const timer = setInterval(() => {
      setProgress(prev => Math.min(prev + 2.5, 100))
    }, 100)

    return () => clearInterval(timer)
  }, [open])

  useEffect(() => {
    if (progress >= 100) setTimeout(onClose, 200)
  }, [progress, onClose])

  if (!open) return null

  return createPortal(
    <Box sx={{ position: "fixed", top: 20, right: 20, zIndex: 9999, minWidth: 300 }}>
      <Paper elevation={8} sx={{
        bgcolor: theme.palette.mode === "dark" ? "#1976d2" : "#fff",
        color: theme.palette.mode === "dark" ? "#fff" : "#333"
      }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" p={1.5} pr={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle color={theme.palette.mode === "dark" ? "inherit" : "primary"} />
            <Typography fontWeight={500}>{message}</Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <LinearProgress variant="determinate" value={progress} sx={{
          height: 4,
          "& .MuiLinearProgress-bar": {
            bgcolor: theme.palette.mode === "dark" ? "#90caf9" : "#1976d2",
          },
        }} />
      </Paper>
    </Box>,
    document.body
  )
}

const ColorPicker = ({ color, onChange, disabled }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [customColor, setCustomColor] = useState(color)

  const handleColorChange = (newColor) => {
    setCustomColor(newColor)
    onChange(newColor)
  }

  const colors = [
    "#FF0000", "#00FF00", "#0000FF", "#FFFF00",
    "#FF00FF", "#00FFFF", "#FFA500", "#800080",
    "#FFC0CB", "#A52A2A", "#808080", "#000000"
  ]

  return (
    <>
      <Box display="flex" alignItems="center" gap={1} onClick={(e) => !disabled && setAnchorEl(e.currentTarget)} sx={{
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.7 : 1
      }}>
        <Box width={32} height={32} bgcolor={color} borderRadius={1} sx={{
          border: "2px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
          {!color && <PaletteIcon fontSize="small" color="action" />}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {color || "Escolher cor"}
        </Typography>
      </Box>

      <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
        <Box p={2} minWidth={280}>
          <Typography variant="subtitle2" gutterBottom>Escolher Cor</Typography>

          <Box mb={2}>
            <TextField
              type="color"
              value={customColor}
              onChange={(e) => handleColorChange(e.target.value)}
              size="small"
              fullWidth
              label="Cor personalizada"
              InputProps={{ sx: { "& input[type='color']": { height: 40 } } }}
            />
          </Box>

          <Box mb={2}>
            <TextField
              value={customColor}
              onChange={(e) => handleColorChange(e.target.value)}
              size="small"
              fullWidth
              label="Código hexadecimal"
              placeholder="#FF0000"
              inputProps={{ pattern: "^#[0-9A-Fa-f]{6}$", maxLength: 7 }}
            />
          </Box>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Cores predefinidas:
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {colors.map((c) => (
              <Box
                key={c}
                width={24}
                height={24}
                bgcolor={c}
                borderRadius="50%"
                onClick={() => handleColorChange(c)}
                sx={{
                  cursor: "pointer",
                  border: customColor === c ? "2px solid #000" : "1px solid #ccc",
                  "&:hover": { transform: "scale(1.1)" }
                }}
              />
            ))}
          </Box>

          <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
            <Button size="small" onClick={() => setAnchorEl(null)}>Cancelar</Button>
            <Button size="small" variant="contained" onClick={() => setAnchorEl(null)}>Aplicar</Button>
          </Box>
        </Box>
      </Popover>
    </>
  )
}

export default function AnchorDialog({ open, onClose, deviceName, position, device, dispatch }) {
  const [lockOnExit, setLockOnExit] = useState(false)
  const [diameter, setDiameter] = useState(50)
  const [color, setColor] = useState("#FF0000")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [showSuccessToast, setShowSuccessToast] = useState(false)
  const [toastMessage, setToastMessage] = useState("")

  const playSound = () => {
    try {
      new Audio("./resources/createFence.mp3").play().catch(() => {
        new Audio("./src/resources/createFence.mp3").play().catch(console.warn)
      })
    } catch (error) {
      console.warn("Erro no áudio:", error)
    }
  }

  const refreshAll = async () => {
    try {
      const [geofencesRes, devicesRes] = await Promise.all([
        fetch('/api/geofences'),
        fetch('/api/devices')
      ])
      if (geofencesRes.ok) dispatch(geofencesActions.refresh(await geofencesRes.json()))
      if (devicesRes.ok) dispatch(devicesActions.refresh(await devicesRes.json()))
    } catch (error) {
      console.error("Erro ao atualizar estados:", error)
    }
  }

  const handleCreate = async () => {
    if (!device?.id) return setError("Dispositivo inválido")
    if (!position) return setError("Posição indisponível")

    setLoading(true)
    setError("")

    try {
      // Validações
      const lat = parseFloat(position.latitude)
      const lng = parseFloat(position.longitude)
      if (isNaN(lat) || isNaN(lng)) throw new Error("Coordenadas inválidas")
      if (!/^#[0-9A-Fa-f]{6}$/.test(color)) throw new Error("Cor inválida")

      // Criar geofence
      const geofenceRes = await fetch("/api/geofences", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: `Âncora ${device.name}`,
          area: `CIRCLE (${lat.toFixed(6)} ${lng.toFixed(6)}, ${diameter})`,
          attributes: { lockOnExit, color, deviceId: device.id }
        })
      })

      if (!geofenceRes.ok) throw new Error("Falha ao criar geofence")
      const geofence = await geofenceRes.json()

      // Operações em paralelo
      await Promise.all([
        fetch("/api/permissions", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ deviceId: device.id, geofenceId: geofence.id })
        }),
        fetch(`/api/devices/${device.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...device,
            attributes: {
              ...device.attributes,
              fence_id: geofence.id,
              lockOnExit,
              hasAnchor: true,
              lastAnchorUpdate: new Date().toISOString()
            }
          })
        })
      ])

      await refreshAll()
      playSound()
      setToastMessage(`Âncora ativada para ${deviceName}!`)
      setShowSuccessToast(true)
      setTimeout(onClose, 300)
    } catch (err) {
      console.error("Erro:", err)
      setError(err.message || "Erro na criação")
      await refreshAll()
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <AnchorIcon fontSize="small" />
              <Typography variant="h6">Criar Âncora</Typography>
            </Box>
            <IconButton onClick={onClose} disabled={loading}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2">Dispositivo: </Typography>
            <Chip label={deviceName} size="small" />
          </Box>

          {position && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2">Posição atual: </Typography>
              <Typography variant="caption">
                Lat: {parseFloat(position.latitude).toFixed(6)}, Lng: {parseFloat(position.longitude).toFixed(6)}
              </Typography>
            </Box>
          )}

          <FormControlLabel
            control={<Checkbox checked={lockOnExit} onChange={(e) => setLockOnExit(e.target.checked)} disabled={loading} />}
            label="Bloquear veículo ao sair"
          />

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Diâmetro (metros)
          </Typography>
          <Box display="flex" gap={1} mb={2}>
            {[50, 100, 300, 500].map((size) => (
              <Chip
                key={size}
                label={`${size}m`}
                onClick={() => setDiameter(size)}
                variant={diameter === size ? "filled" : "outlined"}
                disabled={loading}
              />
            ))}
          </Box>

          <Typography variant="subtitle2" gutterBottom>
            Cor da Âncora
          </Typography>
          <Box mb={2}>
            <ColorPicker color={color} onChange={setColor} disabled={loading} />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={loading || !position}
            startIcon={loading ? <CircularProgress size={20} /> : <AnchorIcon />}
          >
            {loading ? "Criando..." : "Criar Âncora"}
          </Button>
        </DialogActions>
      </Dialog>

      <SuccessToast open={showSuccessToast} message={toastMessage} onClose={() => setShowSuccessToast(false)} />
    </>
  )
}